import {
  LookupTableItem,
  NestedLookupTableItem,
  SearchResponseRecordType,
  SearchRouteTypes,
} from "@/types";

export interface Search {
  currentSearchQuery: SearchQuery;
  cachedResults: SearchItem[][];
  keepCachedResults: boolean;
  autocomplete: {
    isLoading: boolean;
    results: SearchItem[];
  };
  isLoading: boolean;
  keepKeyword: boolean;
}

export interface SearchItem {
  subscriptionModules: number[];
  id: number;
  briefcaseId: number;
  positionSummary: string;
  preferredNameDisplay: string;
  title: string;
  politicalParty: string;
  quickSummary: string;
  headshot: string;
  addedToBriefcase: boolean;
  isFormer: boolean;
  type: SearchResponseRecordType;
}

export interface SearchResultsPagination {
  pageSize: number;
  page: number;
  totalPages: number;
  totalHits: number;
  order: SearchOrder;
}

export enum SearchOrder {
  DEFAULT = "relevance",
  RELEVANCE = "relevance",
  ALPHA_DESC = "alphabetical_desc",
  ALPHA_ASC = "alphabetical_asc",
}

export enum ProfileView {
  DEFAULT = "list",
  CARD = "card",
  LIST = "list",
}

export interface SearchQueryUpdatePayload {
  field: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

// Local search variables
export interface SearchQuery {
  // Search configuration
  page: number;
  pageSize: number;
  order: SearchOrder;
  view: ProfileView; // "" = default (card) / "card" / "list"
  // override flag to hide pay-walled profiles, otherwise fallback to user preference
  hidePayWalledProfiles: boolean | undefined;

  // Local variables
  autoCompleteKeyword: string;

  // Vignette simple search
  keyword: string;

  // Vignette advanced search
  currentOfficeCongressionalDistrict: LookupTableItem;
  currentOfficeStates: string[];
  politicalParty: string;
  religion: LookupTableItem;
  ethnicity: LookupTableItem;
  gender: string;
  state: LookupTableItem;
  country: LookupTableItem;
  editorialGroup: LookupTableItem;
  profileTypes: NestedLookupTableItem[];
  keyPolicyAreas: NestedLookupTableItem[];
  industries: NestedLookupTableItem[];
  committees: NestedLookupTableItem[];
  committee: NestedLookupTableItem[];
  caucuses: NestedLookupTableItem[];
  agencies: string[];
  city: string;
  education: string;
  includeFormer: boolean;
  // Birthdate
  birthYear: LookupTableItem;
  age: LookupTableItem;
  birthDateOperator: LookupTableItem;
  // Special Interest Groups - NestedLookupTableItem but nullable
  sig: NestedLookupTableItem | null;
  sigOperator: LookupTableItem;
  sigRating: LookupTableItem;
  // congressional/federal advanced
  appointment: string[];
  appointment2: string;
  caucus: LookupTableItem[];
  degree: string;
  district: string[];
  interest: string[];
  interest2: LookupTableItem[];
  jobtitle: string[];
  jobtitle2: string[];
  jobtitle2_option: string;
  majmin: string;
  office_location: string[];
  offset: string;
  party: string;
  school: string;
  topagency: string;
  topagency2: string;
  type: string;

  // congressional advanced search
  office: string[];
  // federal advanced search
  agency: string[];
}

/**
 * This type is used to get the keys of a type that are of a certain type.
 * @example
 * type TableItemKeys = KeysOfType<SearchQuery, LookupTableItem>;
 * // TableItemKeys would be all of the keys in SearchQuery that are of type LookupTableItem
 * @param T - The type to get the keys of.
 * @param U - The type to check the keys of T against.
 */
type KeysOfType<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never;
}[keyof T];

type AllTableItems =
  | LookupTableItem
  | NestedLookupTableItem
  | LookupTableItem[]
  | NestedLookupTableItem[];
export type SearchQueryTableItemKeys = KeysOfType<SearchQuery, AllTableItems>;

// This is the parsed and stringified version of SearchQuery object, closer to what API is expecting for each filter.
// SearchQuery object is used for UI components.
// SearchQueryStrings is used for making local comparisons and API requests easier.
export interface SearchQueryStrings {
  [key: string]: string | string[] | number | boolean | undefined;
  // keyword search query and search configurations
  q: string;
  page: number; // >= 0
  pageSize: number; // backend default = 20, we use 12
  order: SearchOrder; // "" = default (relevance) / "relevance" / "alphabetical"
  view: ProfileView; // "" = default (card) / "card" / "list"

  // user preferences
  hidePayWalledProfiles: boolean | undefined;

  // advanced search queries
  // TODO: it will require input of the form "NY-12" for a congressional district.
  current_office_congressional_district: string; // LookupTables.states, single state ID
  gender: string; // LookupTables.genders
  political_party: string; // LookupTables.politicalParties
  religion: string; // LookupTables.religions
  ethnicity: string; // LookupTables.ethnicities
  current_office_state_abbr: string; // LookupTables.states, IDs
  city: string; // text
  state: string; // LookupTables.states
  country: string; // LookupTables.countries
  education: string; // text
  editorial_group: string; // LookupTables.editorialGroups, single ID
  profile_types: string; // LookupTables.profileTypes, comma separated IDs
  key_policy_areas: string; // keyPolicyAreasLookupTable, comma separated IDs
  industry: string; // industryLookupTable, comma separated IDs
  include_former: boolean; // Whether to include archived profiles
  committees: string; // committeesLookupTable, comma separated IDs
  caucuses: string; // caucusesLookupTable, comma separated IDs
  agency: string; // one of four main agency types (WH, Cabinet, Fed Ex, Fed Leg), comma separated IDs
  agencies: string; // agenciesFlat lookup table, comma separated IDs
  // Special Interest Groups
  sig_id: string; // sigsLookupTable, (ID)
  sig_operator: string; // LookupTables.sigOperator
  sig_rating: string; // LookupTables.sigRatingOptions
  // UI selections - mapped to birth_year
  birth_year_selection: string; // LookupTables.birthYearOptions, calculated in ApiService, YYYY (eq), <YYYY (lt), >YYYY (gte)
  age_selection: string; // LookupTables.ageOptions
  birth_date_operator: string; // LookupTables.birthDateOperator
  // congressional/federal advanced
  appointment: string; // text
  appointment2: string; // text
  caucus: string; // caucusesLookupTable, comma separated IDs
  degree: string; // text
  district: string; // text
  interest: string; // text
  interest2: string; // text
  jobtitle: string; // text
  jobtitle2: string; // text
  jobtitle2_option: string; // LookupTables.jobTitle2Options
  majmin: string; // text
  office_location: string; // text
  office: string;
  party: string; // LookupTables.politicalParties
  topagency: string; // text
  topagency2: string; // text
  type: string; // LookupTables.profileTypes
}

export enum SearchTypes {
  VIGNETTE = "vignette-search",
  CONGRESSIONAL = "congressional-search",
  FEDERAL = "federal-search",
}

export type SearchSuggestions = {
  [K in SearchRouteTypes]: string;
};
